import { type CSSProperties, type RefObject, useEffect, useMemo, useState } from 'react';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { MarketCountryCode } from '@pkgs/api';

import { useSettings } from '@/hooks/useSettings';

type UseHeaderHeightParams = {
  elementClassName: string;
  containerRef: RefObject<HTMLDivElement | null>;
};

export const useHeaderHeight = ({ elementClassName, containerRef }: UseHeaderHeightParams): CSSProperties => {
  const { countryCode } = useSettings();
  const { breakpoint = 0 } = useBreakpoint();
  const [measuredHeaderHeight, setMeasuredHeaderHeight] = useState<number>(0);

  useEffect(() => {
    const containerElement = containerRef.current;
    const measuredElement = containerElement?.querySelector<HTMLElement>(elementClassName);

    if (!measuredElement || !measuredElement.isConnected) return;

    const updateElementHeight = () => {
      const nextHeight = Math.round(measuredElement.getBoundingClientRect().height);

      if (nextHeight <= 0) return;

      setMeasuredHeaderHeight((previousHeight) => (previousHeight === nextHeight ? previousHeight : nextHeight));
    };

    updateElementHeight();

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(() => {
      updateElementHeight();
    });

    resizeObserver.observe(measuredElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, elementClassName]);

  const headerHeight = useMemo(() => {
    if (breakpoint < Breakpoints.TABLET_PORTRAIT) {
      return 57;
    }

    if (breakpoint < Breakpoints.TABLET_LANDSCAPE) {
      return countryCode === MarketCountryCode['private-office'] ? 57 : 77;
    }

    return measuredHeaderHeight;
  }, [breakpoint, countryCode, measuredHeaderHeight]);

  return useMemo(
    () =>
      ({
        '--header-height': `${headerHeight}px`,
      }) as CSSProperties,
    [headerHeight],
  );
};
